/**
 * Typed API client — wraps fetch() with auth headers and base URL.
 * Used by all components that talk to the backend.
 */

import { supabase } from './supabase';

const BASE = import.meta.env.VITE_API_BASE_URL as string | undefined;
// In dev, Vite proxy handles /api → localhost:3001, so BASE can be empty.
// In prod, set VITE_API_BASE_URL to the Railway backend URL.
const API_BASE = BASE ?? '';

async function getAuthHeader(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

export class ApiError extends Error {
  status: number;
  code:   string | undefined;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name   = 'ApiError';
    this.status = status;
    this.code   = code;
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const authHeaders = await getAuthHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  });

  const data = await res.json() as T & { error?: string; code?: string };

  if (!res.ok) {
    throw new ApiError(
      data.error ?? `API error ${res.status}`,
      res.status,
      data.code,
    );
  }

  return data;
}
